import { Box, BoxProps, Flex } from '@library/uikit'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import { FC, useEffect, useRef } from 'react'
import { useAppDispatch } from 'state'
import { useTryVideoNftMedia } from 'state/nftMarket/hooks'
import { setTryVideoNftMedia } from 'state/nftMarket/reducer'
import { Image, NftToken } from 'state/nftMarket/types'
import styled from 'styled-components'
import { RoundedImage } from '../Collection/IndividualNFTPage/shared/styles'

const StyledAspectRatio = styled(Box)`
  position: absolute;
  inset: 0;
`

export const Avatar = styled(Flex)`
  overflow: hidden;
  box-sizing: border-box;
  background-color: #141e29;
  border: 2px solid #cccccc;
  border-radius: 50%;
`

export const AspectRatio = ({ ratio, children, ...props }) => (
  <Box width="100%" height={0} pb={`${100 / ratio}%`} position="relative" {...props}>
    <StyledAspectRatio>{children}</StyledAspectRatio>
  </Box>
)

const NFTMedia: FC<
  {
    nft?: NftToken | { name: string; image: Image; animationUrl?: string }
    as?: any
    width: number
    height: number
    preview?: boolean
  } & Omit<BoxProps, 'width' | 'height' | 'as'>
> = ({ width, height, preview, nft, borderRadius = 'default', as, ...props }) => {
  const dispatch = useAppDispatch()
  const tryVideoNftMedia = useTryVideoNftMedia()
  const vidRef = useRef(null)
  const { observerRef, isIntersecting } = useIntersectionObserver()

  useEffect(() => {
    if (vidRef.current) {
      if (isIntersecting) {
        vidRef.current.play().catch((error) => {
          if (error instanceof DOMException && error.name === 'NotAllowedError') {
            dispatch(setTryVideoNftMedia(false))
          }
        })
      } else {
        vidRef.current.pause()
      }
    }
  }, [dispatch, isIntersecting])

  if (tryVideoNftMedia && (nft?.image?.webm || nft?.image?.mp4)) {
    return (
      <AspectRatio ratio={width / height} {...props}>
        <div ref={observerRef} />
        <Box ref={vidRef} borderRadius={borderRadius} as="video" width="100%" height="100%" muted loop playsInline>
          <source src={nft.image.webm} type="video/webm" />
          <source src={nft.image.mp4} type="video/mp4" />
        </Box>
      </AspectRatio>
    )
  }

  return (
    <RoundedImage
      width={width}
      height={height}
      src={
        preview
          ? nft?.image?.preview || nft?.image?.original
          : nft?.image?.original || nft?.image?.preview || nft?.image?.thumbnail
      }
      alt={nft?.name}
      as={as}
      borderRadius={borderRadius}
      {...props}
    />
  )
}

export default NFTMedia
